import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ExploreIcon from '@mui/icons-material/Explore';
import { useNavigate } from 'react-router-dom';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AppConfig from '../services/app-config';
import AuthService from '../services/auth';

export default function AppIssuesHelp({ setNavbarState, navbarState }) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [reporting, setReporting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [showReportForm, setShowReportForm] = React.useState(false);
  const [reportText, setReportText] = React.useState('');
  const [reportSubmitted, setReportSubmitted] = React.useState(false);
  const [selectedIssue, setSelectedIssue] = React.useState(null);

  const fetchResults = React.useCallback((value) => {
    setLoading(true);
    setError('');
    const role = AuthService.getCurrentUserRole();
    apiClient
      .get(`appissues/search?query=${encodeURIComponent(value || '')}&role=${encodeURIComponent(role || '')}`, {}, { action: 'appissues_search' })
      .then((response) => {
        if (response.status === 200) {
          setResults(response.data.data?.results || []);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (shouldRedirectToLogin(err)) {
          window.location.replace('/login');
          return;
        }
        setError(getErrorMessage(err));
        setLoading(false);
      });
  }, []);

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
  }, [setNavbarState, navbarState]);

  React.useEffect(() => {
    const debounce = setTimeout(() => {
      fetchResults(query);
    }, 250);
    return () => clearTimeout(debounce);
  }, [query, fetchResults]);

  const handleReport = React.useCallback(() => {
    if (!reportText.trim()) {
      setSnackbar({ open: true, message: 'Describe el problema antes de enviar.', severity: 'warning' });
      return;
    }
    setReporting(true);
    const metadata = {
      device_platform: AppConfig.getDevicePlatform(),
      user_agent: navigator.userAgent,
      app_version: process.env.REACT_APP_APP_VERSION || 'unknown',
      is_pwa: AppConfig.isInstalled(),
      language: navigator.language,
      screen: `${window.innerWidth}x${window.innerHeight}`,
    };
    apiClient
      .post('appissues/report', { description: reportText.trim(), metadata }, {}, { action: 'appissues_report' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Gracias, recibimos tu reporte.', severity: 'success' });
          setReportSubmitted(true);
        }
        setReporting(false);
      })
      .catch((err) => {
        if (shouldRedirectToLogin(err)) {
          window.location.replace('/login');
          return;
        }
        setSnackbar({ open: true, message: getErrorMessage(err), severity: 'error' });
        setReporting(false);
      });
  }, [reportText]);

  const handleCloseReportForm = () => {
    setShowReportForm(false);
    setReportText('');
    setReportSubmitted(false);
  };

  const buildSteps = (issue) => {
    if (!issue || !Array.isArray(issue.steps)) return [];
    return issue.steps.map((step) => {
      if (typeof step === 'string') {
        return { text: step, image: null };
      }
      return { text: step.text || '', image: step.image || null };
    }).filter((step) => step.text.trim());
  };

  const apiUrl = process.env.REACT_APP_MGO_API_URL;
  const stepImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${apiUrl}/appissues/media/${imagePath}`;
  };

  return (
    <Container maxWidth="md" sx={{ pb: 6 }}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ pt: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Problemas con el app
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Busca soluciones rapidas o reporta un problema si no lo encuentras.
        </Typography>

        <TextField
          fullWidth
          label="Buscar un problema"
          placeholder="Ej. no puedo iniciar sesion"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          fullWidth
          variant="outlined"
          color="primary"
          size="large"
          startIcon={<ExploreIcon />}
          sx={{ fontWeight: 700, py: 1.6, borderRadius: 3, mb: 2 }}
          onClick={() => navigate('/tour')}
        >
          Ver Tour de la App
        </Button>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {results.map((entry) => (
            <Card
              key={entry.id}
              variant="outlined"
              sx={{ borderRadius: 3, cursor: 'pointer' }}
              onClick={() => setSelectedIssue(entry)}
            >
              <CardContent sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {entry.title}
                  </Typography>
                  {entry.source && (
                    <Chip
                      size="small"
                      label={entry.source === 'seed' ? 'Guia' : 'Reportado'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {entry.summary}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            size="large"
            sx={{ fontWeight: 700, py: 1.6, borderRadius: 3 }}
            onClick={() => setShowReportForm(true)}
          >
            No encuentro el problema
          </Button>
        </Box>
      </Box>

      <Dialog open={showReportForm} onClose={handleCloseReportForm} fullScreen>
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Reportar problema
          </Typography>
          <IconButton
            aria-label="cerrar"
            onClick={handleCloseReportForm}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!reportSubmitted && (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Describe tu problema"
                placeholder="Cuentanos que esta pasando, que intentaste y cualquier detalle que nos ayude."
                value={reportText}
                onChange={(event) => setReportText(event.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{ fontWeight: 700, borderRadius: 3 }}
                onClick={handleReport}
                disabled={reporting}
              >
                {reporting ? 'Enviando...' : 'Enviar reporte'}
              </Button>
            </Box>
          )}

          {reportSubmitted && (
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Reporte recibido
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Gracias por avisarnos. Muy pronto un miembro del equipo revisara tu reporte y te dara seguimiento.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ fontWeight: 700, borderRadius: 3, px: 4 }}
                onClick={handleCloseReportForm}
              >
                Listo
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedIssue)}
        onClose={() => setSelectedIssue(null)}
        fullWidth
        maxWidth="sm"
      >
        {selectedIssue && (() => {
          const steps = buildSteps(selectedIssue);
          return (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedIssue.title}
              </Typography>
              <IconButton
                aria-label="cerrar"
                onClick={() => setSelectedIssue(null)}
                sx={{ position: 'absolute', right: 12, top: 12 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedIssue.summary}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {steps.map((step, idx) => (
                  <Box key={`step-${idx}`} sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.8 }} />
                      {idx < steps.length - 1 && (
                        <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'grey.300', mt: 0.5 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, pb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Paso {idx + 1}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {step.text}
                      </Typography>
                      {step.image && (
                        <Box
                          component="img"
                          src={stepImageUrl(step.image)}
                          alt={`Paso ${idx + 1}`}
                          sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </DialogContent>
          </>
        );
        })()}
      </Dialog>
    </Container>
  );
}
